import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";
import { useCreateCustomerPortal, useInvoicePaymentLink } from "@/queries/organizations";
import {
  getPaymentIssueMessage,
  getPaymentIssueAction,
  getPaymentIssueButtonLabel,
} from "@/utils/subscriptionStatus";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useAuth } from "@/context/auth-context";

const PAYMENT_DIALOG_SHOWN_KEY = "payment_issue_dialog_shown";

export const PaymentIssueLoginDialog = () => {
  const { stripe_subscription_status, hasPaymentIssue, isLoadingSubscriptionStatus } =
    useOrganizationSubscriptionData();

  const auth = useAuth();
  const isLoggedIn = !!auth?.userToken && !!auth.user;

  const [isOpen, setIsOpen] = useState(false);
  const [hasShownInSession, setHasShownInSession] = useState(false);

  const { mutate: createCustomerPortal, isLoading: isLoadingPortal } = useCreateCustomerPortal();
  const { mutate: getInvoicePaymentLink, isLoading: isLoadingInvoice } = useInvoicePaymentLink();

  const isLoading = isLoadingPortal || isLoadingInvoice;

  useEffect(() => {
    if (
      isLoggedIn &&
      hasPaymentIssue &&
      stripe_subscription_status &&
      !isLoadingSubscriptionStatus &&
      !hasShownInSession
    ) {
      const sessionKey = `${PAYMENT_DIALOG_SHOWN_KEY}_${stripe_subscription_status}`;
      const hasShown = sessionStorage.getItem(sessionKey) === "true";

      if (!hasShown) {
        setIsOpen(true);
        sessionStorage.setItem(sessionKey, "true");
        setHasShownInSession(true);
      }
    }
  }, [
    isLoggedIn,
    hasPaymentIssue,
    stripe_subscription_status,
    isLoadingSubscriptionStatus,
    hasShownInSession,
  ]);

  if (!hasPaymentIssue || !stripe_subscription_status || !isOpen) {
    return null;
  }

  const message = getPaymentIssueMessage(stripe_subscription_status);
  const action = getPaymentIssueAction(stripe_subscription_status);
  const buttonLabel = getPaymentIssueButtonLabel(stripe_subscription_status);

  const handleAction = () => {
    if (action === "pay_invoice") {
      getInvoicePaymentLink();
    } else if (action === "update_payment_method") {
      createCustomerPortal({ return_url: window.location.href });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <DialogTitle>Payment Issue Detected</DialogTitle>
          </div>
          <DialogDescription className="pt-2">{message}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-2">
          <Button onClick={handleAction} disabled={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner size={4} />
                <span className="ml-2">Loading...</span>
              </>
            ) : (
              buttonLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
